import React, { FC } from 'react';
import { Popover, Tooltip } from 'antd';

// RouteComponentProps/withRouter removed in v6

import { Text } from '../../../common/components';
import { ConstraintsViewerType } from '../../../common/components/ConstraintsViewer/ConstraintsViewer';
import { ExperimentIcon } from '../../../common/Icons';
import { Experiment } from '../../../lib/PlannerDTOs';
import { TreatmentIds } from '../../ExperimentFormV2/helper/consts';
import { VariableSearchColumn } from './VariablesSearch';
import {
  TOTAL_RETURNED_RESULT,
  TypeIDWrapper,
  ValueWrapper,
} from './VariablesSearchRecords';
import {
  hasMatchingConstraints,
  renderConstraintPopover,
} from './VariablesSearchRollout';
import { getUrlBasedOffOriginAndDomainFamily } from '@/utils/url';

interface OwnProps {
  variable: any;
  matchingExperiments: Map<number, Experiment>;
  colIndex: number;
  columns: VariableSearchColumn[];
  renderAll?: boolean;
}

type VariablesSearchExperimentProps = OwnProps;

export const filterExperimentsMatchingFacet = (
  experiments: Experiment[],
  columns: VariableSearchColumn[],
  colIndex: number,
  getAll: boolean = false,
): Map<number, Experiment> => {
  const matchingExperiments = new Map<number, Experiment>();

  if (!experiments?.length) return matchingExperiments;

  for (let index = 0; index < experiments.length; index += 1) {
    if (
      Array.from(matchingExperiments.keys())?.length >= TOTAL_RETURNED_RESULT &&
      !getAll
    )
      break;

    const experiment = experiments[index];

    if (hasMatchingConstraints(experiment.constraints, columns, colIndex))
      matchingExperiments.set(index + 1, experiment);
  }

  return matchingExperiments;
};

const renderConstraintIcon = (
  variable,
  experiment: Experiment,
  index: number,
) => {
  return (
    <Popover
      color="#FFF"
      content={renderConstraintPopover(
        variable,
        experiment,
        index,
        ConstraintsViewerType.EXPERIMENT,
      )}
      placement="left"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 8,
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <g clipPath="url(#clip0_3868_32853)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14ZM15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8ZM8 5.75C8.41421 5.75 8.75 5.41421 8.75 5C8.75 4.58579 8.41421 4.25 8 4.25C7.58579 4.25 7.25 4.58579 7.25 5C7.25 5.41421 7.58579 5.75 8 5.75ZM8.55001 6.5H7.45001L7.45001 11.5H8.55001V6.5Z"
              fill="#1A1A1A"
            />
          </g>
          <defs>
            <clipPath id="clip0_3868_32853">
              <rect width="16" height="16" fill="white" />
            </clipPath>
          </defs>
        </svg>
      </div>
    </Popover>
  );
};

export const controlTreatmentRenderer = (
  variable,
  experiment: Experiment,
  index: number,
  renderWithoutTooltip: boolean = false,
) => {
  const experimentVariables = experiment?.variables;

  if (!experimentVariables) return null;

  const matchingExperimentVariable = experimentVariables.filter(
    (experimentVariable) => {
      return experimentVariable.name === variable.name;
    },
  );

  if (!matchingExperimentVariable?.length) return null;

  let items = [];

  matchingExperimentVariable.forEach(
    (experimentVariable, variableIndex: number) => {
      experimentVariable?.choices.forEach((choice, choiceIndex: number) => {
        items.push(
          <div
            key={`variables-search-variable-${variableIndex}-choice-${choiceIndex}`}
            style={{
              border: '1px solid #E8E8E8',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Tooltip
              title={
                choiceIndex === 0
                  ? 'Control'
                  : `Treatment ${TreatmentIds?.[choiceIndex]}`
              }
            >
              <TypeIDWrapper experimentchoiceindex={choiceIndex}>
                {choiceIndex === 0 ? 'C' : TreatmentIds?.[choiceIndex]}
              </TypeIDWrapper>
            </Tooltip>
            <ValueWrapper
              style={{
                width: 'calc(100% - 72px)',
              }}
            >
              <div
                style={{
                  padding: '0 8px',
                  width: '100%',
                }}
              >
                <Text ellipsis>{choice.string || choice.value}</Text>
              </div>
            </ValueWrapper>
            {!renderWithoutTooltip &&
              renderConstraintIcon(experimentVariable, experiment, index)}
          </div>,
        );
      });
    },
  );

  return items;
};

const VariablesSearchExperiment: FC<VariablesSearchExperimentProps> = ({
  variable,
  matchingExperiments,
  renderAll = false,
}) => {
  const experimentRenderer = (experiment: Experiment, index: number) => {
    return (
      <div
        key={`variables-search-experiment-${experiment.name.split('.')?.[1]}`}
        className="flex-row-center-aligned half-gap w100 open-object-experiment-wrapper"
        onClick={() => {
          window.open(
            getUrlBasedOffOriginAndDomainFamily(
              `/experiments/${experiment.name}/overview`,
            ),
            '_blank',
          );
        }}
      >
        <div
          style={{
            display: 'flex',
            width: '32px',
            minWidth: '32px',
            minHeight: '32px',
            borderRadius: '8px',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #E8E8E8',
            fontSize: '12px',
            fontWeight: 700,
            alignSelf: 'stretch',
          }}
        >
          {index}
        </div>

        <div
          className="flex-col half-gap"
          style={{
            width: 'calc(100% - 40px)',
          }}
        >
          {controlTreatmentRenderer(variable, experiment, index)}
        </div>
      </div>
    );
  };

  const experimentsRenderer = (experiments: Map<number, Experiment>) => {
    const items = [];
    let countTracker = 0;

    experiments.forEach((experiment: Experiment, index: number) => {
      if (countTracker === TOTAL_RETURNED_RESULT - 1 && !renderAll) return;

      items.push(experimentRenderer(experiment, index));

      countTracker += 1;
    });

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          flexGrow: 1,
          width: 'calc(100% - 40px)',
        }}
      >
        {items}
      </div>
    );
  };

  const renderContent = () => {
    if (!Array.from(matchingExperiments.keys())?.length) return null;

    return (
      <div className="flex-row-center-aligned half-gap w100">
        <Tooltip title="Experiment" placement="left" zIndex={1}>
          <div
            style={{
              display: 'flex',
              width: '32px',
              minWidth: '32px',
              backgroundColor: 'rgba(19, 111, 216, 0.10)',
              borderRadius: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              alignSelf: 'stretch',
            }}
          >
            <ExperimentIcon />
          </div>
        </Tooltip>

        {experimentsRenderer(matchingExperiments)}
      </div>
    );
  };

  return renderContent();
};

export default VariablesSearchExperiment;
